import { useCallback, useEffect, useRef, useState } from "react";

interface UseTutorialAnchorOptions {
	active?: boolean;
	anchorSelector?: string;
	animateTarget?: boolean;
	compactLandscapeTargetVisibleRatio?: number;
	hiddenDescendantSelector?: string;
	panelAnchorOffsetEm?: number;
	panelPlacement?: "above" | "below";
	panelSelector?: string;
	portraitScrollOffsetSelector?: string;
	scrollMode?: "center" | "tutorial-offer" | "tutorial-offer-side";
	targetId?: string;
	targetResolver?: () => HTMLElement | null;
	targetSelector?: string;
	shouldScroll?: boolean;
}

export const useTutorialAnchor = ({
	active = true,
	anchorSelector,
	animateTarget = false,
	compactLandscapeTargetVisibleRatio = 1,
	hiddenDescendantSelector,
	panelAnchorOffsetEm = 0,
	panelPlacement = "above",
	panelSelector,
	portraitScrollOffsetSelector,
	scrollMode = "center",
	targetId,
	targetResolver,
	targetSelector,
	shouldScroll = true
}: UseTutorialAnchorOptions) => {
	const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
	const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
	const frameRef = useRef<number | null>(null);
	const positionFrameRef = useRef<number | null>(null);
	const settleTimeoutRef = useRef<number | null>(null);
	const targetRef = useRef<HTMLElement | null>(null);

	const cancelScheduledMeasure = useCallback(() => {
		if (frameRef.current !== null) {
			cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
		}
	}, []);

	const scheduleMeasure = useCallback(() => {
		cancelScheduledMeasure();
		frameRef.current = requestAnimationFrame(() => {
			frameRef.current = requestAnimationFrame(() => {
				const target = targetResolver?.() ?? targetRef.current;
				targetRef.current = target;
				setTargetElement(target);
				const anchor = anchorSelector
					? target?.querySelector<HTMLElement>(anchorSelector)
					: null;
				setTargetRect((anchor ?? target)?.getBoundingClientRect() ?? null);
				frameRef.current = null;
			});
		});
	}, [anchorSelector, cancelScheduledMeasure, targetResolver]);

	const cancelScheduledPosition = useCallback(() => {
		if (positionFrameRef.current !== null) {
			cancelAnimationFrame(positionFrameRef.current);
			positionFrameRef.current = null;
		}
	}, []);

	const cancelScheduledSettle = useCallback(() => {
		if (settleTimeoutRef.current !== null) {
			window.clearTimeout(settleTimeoutRef.current);
			settleTimeoutRef.current = null;
		}
	}, []);

	const scrollTargetIntoPosition = useCallback(
		(target: HTMLElement) => {
			if (scrollMode === "center") {
				target.scrollIntoView({
					behavior: "auto",
					block: "center",
					inline: "nearest"
				});
				return;
			}

			const rect = target.getBoundingClientRect();
			const viewport = window.visualViewport;
			const viewportTop = viewport?.offsetTop ?? 0;
			const viewportHeight = viewport?.height ?? window.innerHeight;
			const viewportWidth = viewport?.width ?? window.innerWidth;
			const viewportBottom = viewportTop + viewportHeight;
			const portraitScrollOffsetElement =
				portraitScrollOffsetSelector && viewportHeight > viewportWidth
					? document.querySelector<HTMLElement>(portraitScrollOffsetSelector)
					: null;
			const portraitScrollOffsetRect =
				portraitScrollOffsetElement?.getBoundingClientRect();
			const portraitScrollOffset =
				portraitScrollOffsetRect &&
				portraitScrollOffsetRect.height > 0 &&
				portraitScrollOffsetRect.bottom > viewportTop &&
				portraitScrollOffsetRect.top < viewportBottom
					? portraitScrollOffsetRect.height
					: 0;
			const tutorialRoot = document.querySelector<HTMLElement>(
				"[data-tutorial-v2-root]"
			);
			const panel = panelSelector
				? document.querySelector<HTMLElement>(panelSelector)
				: null;
			const oneEm = tutorialRoot
				? parseFloat(window.getComputedStyle(tutorialRoot).fontSize)
				: 10;
			const panelHeight = panel?.getBoundingClientRect().height ?? 0;
			const panelSpace = panelHeight + panelAnchorOffsetEm * oneEm;
			const isCompactLandscape =
				viewportWidth > viewportHeight && viewportHeight <= 500;

			if (scrollMode === "tutorial-offer-side" && isCompactLandscape) {
				const desiredButtonMargin = oneEm;
				let desiredTop =
					viewportTop + Math.max(0, (viewportHeight - rect.height) / 2);
				const purchaseButton = target.querySelector<HTMLElement>(
					anchorSelector ?? "button[id$=-button]"
				);

				if (purchaseButton) {
					const buttonRect = purchaseButton.getBoundingClientRect();
					const buttonBottomAtDesiredTop =
						buttonRect.bottom + desiredTop - rect.top;
					const buttonOverflow =
						buttonBottomAtDesiredTop - (viewportBottom - desiredButtonMargin);

					if (buttonOverflow > 0) {
						desiredTop -= buttonOverflow;
					}
				}

				const scrollBy = rect.top - desiredTop;
				if (Math.abs(scrollBy) > 0.5) {
					const currentScrollTop =
						window.pageYOffset || document.documentElement.scrollTop || 0;
					window.scrollTo({
						top: Math.max(0, currentScrollTop + scrollBy),
						behavior: "auto"
					});
				}
				return;
			}
			const visibleTargetRatio = isCompactLandscape
				? compactLandscapeTargetVisibleRatio
				: 1;
			const visibleTargetHeight = rect.height * visibleTargetRatio;
			const hiddenTargetHeight = rect.height - visibleTargetHeight;
			const groupHeight = panelSpace + visibleTargetHeight;
			const groupTop =
				viewportTop + Math.max(oneEm, (viewportHeight - groupHeight) / 2);
			const desiredTop =
				panelPlacement === "above"
					? groupTop + panelSpace
					: groupTop - hiddenTargetHeight;
			const applicablePortraitScrollOffset =
				groupTop + groupHeight + portraitScrollOffset <= viewportBottom
					? portraitScrollOffset
					: 0;
			const scrollBy = rect.top - desiredTop - applicablePortraitScrollOffset;

			if (Math.abs(scrollBy) > 0.5) {
				const currentScrollTop =
					window.pageYOffset || document.documentElement.scrollTop || 0;
				window.scrollTo({
					top: Math.max(0, currentScrollTop + scrollBy),
					behavior: "auto"
				});
			}
		},
		[
			anchorSelector,
			compactLandscapeTargetVisibleRatio,
			panelAnchorOffsetEm,
			panelPlacement,
			panelSelector,
			portraitScrollOffsetSelector,
			scrollMode
		]
	);

	useEffect(() => {
		if (!active || (!targetId && !targetSelector && !targetResolver)) {
			setTargetElement(null);
			setTargetRect(null);
			return;
		}

		const viewport = window.visualViewport;
		let target: HTMLElement | null = null;
		let hiddenDescendant: HTMLElement | null = null;
		let previousDescendantVisibility = "";
		let observer: MutationObserver | null = null;
		let layoutObserver: ResizeObserver | null = null;

		const connectTarget = () => {
			const nextTarget = targetResolver
				? targetResolver()
				: targetId
				? document.getElementById(targetId)
				: document.querySelector<HTMLElement>(targetSelector as string);

			if (!nextTarget || nextTarget === target) {
				return !!nextTarget;
			}

			target = nextTarget;
			targetRef.current = nextTarget;
			setTargetElement(nextTarget);
			if (animateTarget) {
				nextTarget.classList.add("tutorial-v2-anchor");
			}
			if (hiddenDescendantSelector) {
				hiddenDescendant = nextTarget.querySelector<HTMLElement>(
					hiddenDescendantSelector
				);
				if (hiddenDescendant) {
					previousDescendantVisibility = hiddenDescendant.style.visibility;
					hiddenDescendant.style.visibility = "hidden";
				}
			}

			if (shouldScroll) {
				scrollTargetIntoPosition(nextTarget);
			}

			scheduleMeasure();
			cancelScheduledSettle();
			settleTimeoutRef.current = window.setTimeout(() => {
				const currentTarget = targetResolver?.() ?? targetRef.current;
				if (shouldScroll && currentTarget) {
					scrollTargetIntoPosition(currentTarget);
				}
				scheduleMeasure();
				settleTimeoutRef.current = null;
			}, 250);
			return true;
		};

		if (!connectTarget()) {
			observer = new MutationObserver(() => {
				if (connectTarget()) {
					observer?.disconnect();
					observer = null;
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
		}

		const handleViewportChange = () => {
			cancelScheduledPosition();
			cancelScheduledSettle();

			const reposition = () => {
				const currentTarget = targetResolver?.() ?? targetRef.current;
				targetRef.current = currentTarget;
				setTargetElement(currentTarget);
				if (shouldScroll && currentTarget) {
					scrollTargetIntoPosition(currentTarget);
				}
				scheduleMeasure();
			};

			positionFrameRef.current = requestAnimationFrame(() => {
				positionFrameRef.current = requestAnimationFrame(() => {
					scheduleMeasure();
					positionFrameRef.current = null;
				});
			});

			settleTimeoutRef.current = window.setTimeout(() => {
				reposition();
				settleTimeoutRef.current = null;
			}, 250);
		};

		if (typeof ResizeObserver !== "undefined") {
			layoutObserver = new ResizeObserver(handleViewportChange);
			layoutObserver.observe(document.body);
		}

		window.addEventListener("resize", handleViewportChange);
		window.addEventListener("orientationchange", handleViewportChange);
		window.addEventListener("scroll", scheduleMeasure, true);
		window.addEventListener("transitionend", scheduleMeasure, true);
		viewport?.addEventListener("resize", handleViewportChange);
		viewport?.addEventListener("scroll", scheduleMeasure);

		return () => {
			observer?.disconnect();
			layoutObserver?.disconnect();
			window.removeEventListener("resize", handleViewportChange);
			window.removeEventListener("orientationchange", handleViewportChange);
			window.removeEventListener("scroll", scheduleMeasure, true);
			window.removeEventListener("transitionend", scheduleMeasure, true);
			viewport?.removeEventListener("resize", handleViewportChange);
			viewport?.removeEventListener("scroll", scheduleMeasure);
			cancelScheduledSettle();
			cancelScheduledPosition();
			cancelScheduledMeasure();
			targetRef.current = null;
			target?.classList.remove("tutorial-v2-anchor");
			if (hiddenDescendant) {
				hiddenDescendant.style.visibility = previousDescendantVisibility;
			}
		};
	}, [
		active,
		animateTarget,
		cancelScheduledMeasure,
		cancelScheduledPosition,
		cancelScheduledSettle,
		hiddenDescendantSelector,
		scheduleMeasure,
		scrollTargetIntoPosition,
		shouldScroll,
		targetId,
		targetResolver,
		targetSelector
	]);

	return { scheduleMeasure, targetElement, targetRect };
};
